/**
 * lib/camera.js — pure camera math for the SheetViewer.
 *
 * Extracted from `components/SheetCanvas.jsx` so it can be unit-tested and
 * so future modules (TileManager, DiffLayer, InspectorBridge) compose
 * against a single source of truth.
 *
 * Conventions:
 *   - viewport = { tx, ty, s }   tx/ty in screen px, s = scale multiplier
 *   - dims    = { w, h }         either image natural OR container
 *   - All functions are pure — no DOM, no React, no localStorage.
 *
 * Side-effect storage helpers live in `lib/viewportStore.js`.
 */

// ---------------------------------------------------------------------------
// Scale + fit math
// ---------------------------------------------------------------------------

/**
 * computeFitScale — the scale that fits an image into a container, preserving
 * aspect ratio (`object-fit: contain` equivalent).
 */
export function computeFitScale(imgDims, containerDims) {
  if (!imgDims?.w || !containerDims?.w) return 1;
  const fw = containerDims.w / imgDims.w;
  const fh = containerDims.h / imgDims.h;
  return Math.min(fw, fh);
}

/** Clamp scale into the safe band [fit * 0.5, 8]. */
export function clampScale(s, fitScale, max = 8) {
  return Math.max(fitScale * 0.5, Math.min(max, s));
}

// ---------------------------------------------------------------------------
// Rubber-band + bounds
// ---------------------------------------------------------------------------

const RUBBER_C = 0.55;

/**
 * iOS-style rubber-band easing. At the natural bound, overscroll asymptotes
 * toward (RUBBER_C * dim). Subtle resistance — no cartoon bounce.
 */
export function rubberBand(delta, dim) {
  const d = Math.max(1, dim);
  return (delta * RUBBER_C * d) / (d + RUBBER_C * delta);
}

/**
 * Constrain a translation (tx OR ty) against the container/image dimension,
 * applying rubber-band easing past the natural bounds.
 */
export function constrainTranslation(t, scaled, container) {
  if (!container) return t;
  if (scaled <= container) {
    // Image is smaller than container — centre it, allow elastic drift.
    const center = (container - scaled) / 2;
    const delta = t - center;
    return center + Math.sign(delta) * rubberBand(Math.abs(delta), container);
  }
  const min = container - scaled;
  const max = 0;
  if (t > max) return max + rubberBand(t - max, container);
  if (t < min) return min - rubberBand(min - t, container);
  return t;
}

/**
 * Snap a viewport to its nearest natural bounds (no rubber-band). Used on
 * pointer-up to ease back from overscroll.
 */
export function snapToNaturalBounds(vp, imgDims, containerDims) {
  let { tx, ty } = vp;
  const s = vp.s;
  if (imgDims?.w && containerDims?.w) {
    const imgW = imgDims.w * s;
    if (imgW <= containerDims.w) tx = (containerDims.w - imgW) / 2;
    else tx = Math.min(0, Math.max(containerDims.w - imgW, tx));
  }
  if (imgDims?.h && containerDims?.h) {
    const imgH = imgDims.h * s;
    if (imgH <= containerDims.h) ty = (containerDims.h - imgH) / 2;
    else ty = Math.min(0, Math.max(containerDims.h - imgH, ty));
  }
  return { tx, ty, s };
}

/** True if the viewport is currently overscrolled (outside natural bounds). */
export function isOverscrolled(vp, imgDims, containerDims, eps = 0.5) {
  const target = snapToNaturalBounds(vp, imgDims, containerDims);
  return Math.abs(target.tx - vp.tx) > eps || Math.abs(target.ty - vp.ty) > eps;
}

// ---------------------------------------------------------------------------
// Zoom-around-point (cursor / pinch midpoint anchor)
// ---------------------------------------------------------------------------

/**
 * Compute the viewport translation needed to keep a screen-space point
 * stationary while the scale changes from `oldS` to `newS`.
 *
 * Use case: wheel zoom anchored under the cursor; pinch zoom anchored at the
 * midpoint of two fingers. Without this, the user feels like the drawing
 * "drifts" during zoom — a deal-breaker for physical-feel.
 */
export function zoomAroundPoint({ vp, point, newScale }) {
  // Convert the screen point into image-local coords using the OLD viewport.
  const imageX = (point.x - vp.tx) / vp.s;
  const imageY = (point.y - vp.ty) / vp.s;
  // Recompute tx/ty so the same image-local coord still lands on the same
  // screen-space point at the NEW scale.
  return {
    tx: point.x - imageX * newScale,
    ty: point.y - imageY * newScale,
    s: newScale,
  };
}

/**
 * Centre a normalised point (nx, ny ∈ [0,1]) in the container without
 * changing scale. Used by Revision Review "click change → focus".
 */
export function centerOnNormalizedPoint({ nx, ny, scale, imgDims, containerDims }) {
  const imgX = (nx ?? 0.5) * (imgDims?.w || 0);
  const imgY = (ny ?? 0.5) * (imgDims?.h || 0);
  return {
    tx: (containerDims?.w || 0) / 2 - imgX * scale,
    ty: (containerDims?.h || 0) / 2 - imgY * scale,
    s: scale,
  };
}

// ---------------------------------------------------------------------------
// Initial-fit viewport
// ---------------------------------------------------------------------------

/**
 * Build the default "fit + centre" viewport for a freshly opened sheet.
 */
export function fitAndCenter(imgDims, containerDims) {
  const s = computeFitScale(imgDims, containerDims) || 1;
  const tx = ((containerDims?.w || 0) - (imgDims?.w || 0) * s) / 2;
  const ty = ((containerDims?.h || 0) - (imgDims?.h || 0) * s) / 2;
  return { tx, ty, s };
}

/**
 * Validate a stored viewport. Reject anything wildly out of range vs the
 * current fit (e.g. user switched between portrait/landscape or moved
 * between very-different-sized sheets).
 */
export function isPlausibleViewport(vp, fitScale) {
  if (!vp || !Number.isFinite(vp.s) || !Number.isFinite(vp.tx) || !Number.isFinite(vp.ty)) {
    return false;
  }
  if (!fitScale) return true;
  return vp.s >= fitScale * 0.4 && vp.s <= fitScale * 8;
}
